import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { useMeContext } from '~/modules/me';
import { CostOverrideType } from '../CostOverrideType';
import {
  GET_PAGE_OF_PROJECT_COST_RATES,
  mapToGetPageOfProjectCostRatesVariables
} from './useProjectCostOverridesData';
import { GET_PROJECT_COST_RATE_TABLE_DIMENSIONS } from './useCostOverrideTableDimensions';

export const PUT_PROJECT_COST_RATES = gql`
  mutation putProjectCostRates(
    $projectId: String!
    $dimensionUris: [String!]
    $requiredDimensionUris: [String!]
    $rates: [ProjectRateInput3!]
  ) {
    putProjectCostRates(
      projectId: $projectId
      dimensionUris: $dimensionUris
      requiredDimensionUris: $requiredDimensionUris
      rates: $rates
    ) {
      id
      dimensions {
        dimensionUri
        objectUri
      }
      scheduleEntries {
        amount {
          amount
          currency {
            id
          }
        }
      }
    }
  }
`;

const mapProjectRatesToCostRateInput = (selectedDimensions, projectRates) => {
  const groupMap = projectRates.reduce((acc, rate) => {
    const { groupId } = rate;

    if (!acc[groupId]) {
      acc[groupId] = [];
    }
    acc[groupId].push(rate);

    return acc;
  }, {});

  return Object.values(groupMap).map(groupRates => {
    const firstRate = groupRates[0];
    const dimensions = selectedDimensions
      .filter(d => firstRate[d.id]?.value)
      .map(d => ({
        dimensionUri: d.id,
        objectUri: firstRate[d.id].value.id
      }));

    const entries = groupRates.map(rate => {
      const isPercentMarkup = rate.type === CostOverrideType.PERCENT_MARKUP;
      const hasAmount = rate.amount?.amount > 0;

      return {
        amount: {
          amount: !isPercentMarkup && hasAmount ? rate.amount.amount : 0,
          currency: { id: rate.amount?.currency?.id }
        },
        ...(isPercentMarkup ? { percentMarkup: rate.percent } : {}),
        effectiveDate: rate.effectiveDate
      };
    });

    return { dimensions, entries };
  });
};

export const updateCostRateDimensionsCache = ({
  projectId,
  updatedDimensions
}) => cache => {
  const stored = cache.readQuery({
    query: GET_PROJECT_COST_RATE_TABLE_DIMENSIONS,
    variables: { projectId }
  });

  if (!stored?.project?.costRateTableDimension) return;

  // selectedDimensions are display-layer objects enriched by
  // OVERRIDE_DIMENSION_PROPS (e.g. sortIndex). Strip UI-only fields so the
  // cached shape stays consistent with the GraphQL schema for this query.
  const dimensions = updatedDimensions.map(
    ({ sortIndex, ...dimension }) => dimension
  );

  cache.writeQuery({
    query: GET_PROJECT_COST_RATE_TABLE_DIMENSIONS,
    variables: { projectId },
    data: {
      project: {
        ...stored.project,
        costRateTableDimension: {
          ...stored.project.costRateTableDimension,
          dimensions
        }
      }
    }
  });
};

export const usePutProjectCostRates = () => {
  const me = useMeContext();
  const [putProjectCostRatesMutation, { loading }] = useMutation(
    PUT_PROJECT_COST_RATES
  );

  return {
    putProjectCostRates: async ({
      projectId,
      selectedDimensions,
      projectRates
    }) => {
      const rates = mapProjectRatesToCostRateInput(
        selectedDimensions,
        projectRates
      );
      const dimensionUris = selectedDimensions.map(d => d.id);

      return putProjectCostRatesMutation({
        variables: {
          projectId,
          dimensionUris,
          requiredDimensionUris: [],
          rates
        },
        refetchQueries: [
          {
            query: GET_PAGE_OF_PROJECT_COST_RATES,
            variables: mapToGetPageOfProjectCostRatesVariables(
              projectId,
              dimensionUris,
              me
            )
          }
        ],
        awaitRefetchQueries: true,
        update: updateCostRateDimensionsCache({
          projectId,
          updatedDimensions: selectedDimensions
        })
      });
    },
    isSaving: loading
  };
};
